import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    Paper,
} from "@mui/material";
import { fetchData, HTTP_STATUS, intl, DmsAltBox } from "dmsbasereact";
import { useDmsAnalyticsContext } from "./DmsAnalyticsContext";

export const DmsAnalyticsTable = ({
    url,
    title,
    columns,
    showTotals = true,
    maxRows = 10,
    sx,
    ...props
}) => {
    const { queryParams, refreshKey } = useDmsAnalyticsContext();
    const [data, setData] = useState([]);
    const [totals, setTotals] = useState({});
    const [loading, setLoading] = useState(false);

    const loadData = useCallback(async () => {
        if (!url) return;

        setLoading(true);
        try {
            const separator = url.includes("?") ? "&" : "?";
            const fullUrl = queryParams ? `${url}${separator}${queryParams}` : url;
            const response = await fetchData(fullUrl, "GET", true);

            if (response.status === HTTP_STATUS.OK_200) {
                const responseData = response.data?.data;
                setData(responseData?.items || responseData || []);
                setTotals(responseData?.totals || {});
            }
        } catch (err) {
            console.error(`Error loading data from ${url}:`, err);
            setData([]);
            setTotals({});
        } finally {
            setLoading(false);
        }
    }, [url, queryParams]);

    useEffect(() => {
        loadData();
    }, [loadData, refreshKey]);

    const safeData = Array.isArray(data) ? data : [];
    const displayData = safeData.slice(0, maxRows);

    const formatValue = (value) => {
        if (typeof value === "number") {
            return value.toLocaleString("pt-BR");
        }
        return value || "-";
    };

    return (
        <DmsAltBox.Card sx={{ height: "100%", ...sx }} {...props}>
            <DmsAltBox.Header>
                <Typography variant="h6" sx={{ color: "common.white" }}>
                    {title}
                </Typography>
            </DmsAltBox.Header>
            <DmsAltBox.Body>
                <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
                    {loading ? (
                        <Box sx={{ p: 2 }}>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <Skeleton key={index} variant="rectangular" height={40} sx={{ mb: 1 }} />
                            ))}
                        </Box>
                    ) : displayData.length === 0 ? (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: 200,
                                color: "text.secondary",
                            }}
                        >
                            <Typography variant="body2">
                                {intl.get("DmsAnalytics.NoData").d("Sem dados disponíveis")}
                            </Typography>
                        </Box>
                    ) : (
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {columns.map((col) => (
                                        <TableCell
                                            key={col.field}
                                            align={col.align || "left"}
                                            sx={{
                                                fontWeight: 600,
                                                backgroundColor: "grey.100",
                                            }}
                                        >
                                            {col.header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayData.map((row, index) => (
                                    <TableRow key={row.pk || index} hover>
                                        {columns.map((col) => (
                                            <TableCell key={col.field} align={col.align || "left"}>
                                                {formatValue(row[col.field])}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                                {showTotals && Object.keys(totals).length > 0 && (
                                    <TableRow sx={{ backgroundColor: "grey.200" }}>
                                        {columns.map((col, index) => (
                                            <TableCell
                                                key={col.field}
                                                align={col.align || "left"}
                                                sx={{ fontWeight: 700 }}
                                            >
                                                {index === 0
                                                    ? intl.get("DmsAnalytics.Stock.Total").d("Total")
                                                    : formatValue(totals[col.field])}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </DmsAltBox.Body>
        </DmsAltBox.Card>
    );
};

DmsAnalyticsTable.propTypes = {
    url: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            header: PropTypes.string.isRequired,
            align: PropTypes.oneOf(["left", "center", "right"]),
        })
    ).isRequired,
    showTotals: PropTypes.bool,
    maxRows: PropTypes.number,
    sx: PropTypes.object,
};
